import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Observer, Subject, Subscriber, fromEvent, merge } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { HTTP } from '@awesome-cordova-plugins/http/ngx';
import { Network } from '@awesome-cordova-plugins/network/ngx';

import { take, map } from 'rxjs/operators';

import { AppConstants } from '@unpispas/upp-defs';
import { platformService } from './platform';
import { stateService } from './state';

/** Available HTTP response types. */
export type HttpResponseTypes = "json" | "blob";

/** Available HTTP serializers. */
export type HttpSerializer = "urlencoded" | "json" | "utf8" | "multipart" | "raw";

/**
  @internal
 */
class onlineService {
    private _isOnline = true; 

    private _onOnlineChanged = new Subject <boolean> ();
    public OnOnlineChanged = this._onOnlineChanged.asObservable();
    
    get isOnline(){
        return this._isOnline;
    }
    
    set isOnline(value){
        if (value != this.isOnline){
            this._isOnline = value;
            this._onOnlineChanged.next(this._isOnline);
        }
    }

    private _connect_subscription: any = null;
    private _disconn_subscription: any = null;

    constructor(private http: httpService, private platform: platformService, private network: Network) {
        if (this.platform.is('cordova')){
            this.isOnline = this.network.type !== 'none';

            this._disconn_subscription = this.network.onDisconnect().subscribe(() => {
                console.warn("[local connection - offline]");
                this.isOnline = false;
            });

            this._connect_subscription = this.network.onConnect().subscribe(() => {        
                console.info("[local connection - online]");
                this.isOnline = true;
            });
        }
        else {
            this.isOnline = navigator.onLine;

            this._doObserveOnline().subscribe(() => {
                if (this.isOnline != navigator.onLine){
                    if (this.isOnline){
                        console.warn("[local connection - offline]");
                    }
                    else {
                        console.info("[local connection - online]");
                    }                    
                    this.isOnline = navigator.onLine;
                }
            });  
        }
    }

    OnDestroy(){
        if (this._connect_subscription){
            this._connect_subscription.unsubscribe();
            this._connect_subscription = null;
        }

        if (this._disconn_subscription){
            this._disconn_subscription.unsubscribe();
            this._disconn_subscription = null;
        }
    }

    // https://stackoverflow.com/questions/46598777/check-internet-connection-in-web-using-angular-4    
    private _doObserveOnline() {
        return merge (
            fromEvent(window, 'online').pipe(map(() => true)),
            fromEvent(window, 'offline').pipe(map(() => false)),
            new Observable((sub: Observer<boolean>) => {
                sub.next(navigator.onLine);
                sub.complete();
            })
        );
    }    
    
    private _retries = 0;
    
    public checkOnline(observer: Subscriber <boolean>){
        this.http.check().pipe(take(1)).subscribe({
            next: () => {
                this._retries = 0;

                observer.next(true);
                observer.complete();
                
                if (!this.isOnline){
                    console.info("[server connection - online]");
                }
    
                this.isOnline = true;    
            },
            error: (error) => {
                this._OnCheckFailure(observer, error);
            },
            complete: () => {
                // nothing to do
            }
        });
    }

    private _OnCheckFailure(observer: Subscriber <boolean>, error: any){
        if (++this._retries >= 2){                    
            if (this.isOnline){
                console.warn("[online] exeeced retry count -- now yow are offline");

                this.isOnline = false;

                setTimeout(() => {
                    this.waitOnline();
                }, 500);
            }

            observer.next(false);
            observer.complete();
        }
        else {
            console.warn("[online] server is not responding (retry count: " + this._retries + ")", error);
            setTimeout(() => {
                this.checkOnline(observer);
            }, 250);
        }    
    }
    
    private waitOnline() {
        const observable = new Observable <boolean> ((observer) => {
            this.checkOnline(observer);
        });
                                        
        observable.pipe(take(1)).subscribe({
            next: (data) => {
                if (!data){
                    setTimeout(() => {
                        this.waitOnline()
                    }, 500);
                }    
            },
            error: () => {
                // nothing to do
            },
            complete: () => {
                // nothing to do
            }
        });
    }    
};

/**
 * The `httpService` class provides methods for making HTTP requests and monitoring
 * internet connectivity across multiple platforms (browser and Cordova). It also
 * leverages platform-specific plugins for network state management and performs 
 * HTTP requests accordingly, using Angular's HttpClient for web and Ionic's HTTP plugin 
 * for mobile.
 * 
 * This service includes platform-dependent methods for `GET`, `POST`, `HEAD`, and 
 * connectivity checks, managing the different behaviors required by web browsers 
 * and mobile applications.
 * 
 * Additionally, `httpService` keeps track of the online/offline status and offers an 
 * observable that emits the status whenever it changes. This is useful for components 
 * and other services that need to respond to connectivity changes.
 * 
 * @remarks
 * - For browsers, it uses Angular's `HttpClient`.
 * - For mobile (Cordova), it uses the Ionic HTTP plugin.
 * - Implements `OnDestroy` to handle cleanup and avoid memory leaks.
 * 
 * @example
 * Usage in an Angular component to monitor connectivity:
 * 
 * ```typescript
 * constructor(private httpService: httpService) {
 *   this.httpService.OnOnline?.subscribe(status => {
 *     console.log("Online status: ", status);
 *   });
 * }
 * 
 * this.httpService.get('https://api.example.com/data').subscribe(data => {
 *   console.log(data);
 * });
 * ```
 */
@Injectable({
    providedIn: 'root'
})
export class httpService implements OnDestroy {
    private _online: onlineService | null = null;
    private get online() : onlineService{
        if (this._online == null){
            this._online = new onlineService(this, this.platform, this.network); 
        }
        return this._online;
    }
    
    /**
     * Returns a boolean indicating whether the device is online.
     * @returns {boolean} Internet connection status.
     */    
    get IsOnline() : boolean {
        return this.online.isOnline;
    }

    /**
     * Observable that emits the internet connection status whenever it changes.
     * @returns {Observable<boolean>} Observable of the connection status.
     */    
    get OnOnline() : Observable <boolean> {
        return this.online.OnOnlineChanged;
    }

    /**
     * HTTP service constructor.
     * @param platform Platform service used to detect if the environment is Cordova.
     * @param ionic Ionic HTTP service used for requests on mobile platforms.
     * @param network Network service used to monitor the network connection status.
     * @param angular Angular HTTP client used for requests in browsers.
     */    
    constructor(
        private platform: platformService,
        private ionic: HTTP,
        private network: Network,
        private angular: HttpClient,
        private state: stateService
    ) { 
        this._online = this.online;  // just to initialize
    }

    /**
     * Cleanup method called on service destruction.
     */
    ngOnDestroy(){
        if (this._online){
            this._online.OnDestroy();
            this._online = null;
        }
    }

    /********************************************/
    /* DEBUG TRACES                             */
    /********************************************/
    
    private _dbgTrace = false;
    private debuglog(...args: any[]){
        if (this._dbgTrace){
            console.info(...args);
        }
    }
    
    /********************************************/
    /* DEFAULT HEADER FOR HTTP REQUESTS         */
    /********************************************/
    
    public static get headers() {
        return {
            'Accept': 'text/html',
            'Content-Type': 'application/x-www-form-urlencoded'
        };
    };
    
    private _HttpError(observer: Subscriber <boolean>, error: any){
        let _subscription: any = null;

        const observable = new Observable((_observer) => {
            this.online.checkOnline(_observer);
        });
        
        _subscription = observable.subscribe({
            next: (data) => {
                if (_subscription) {
                    _subscription.unsubscribe();
                }
                if (data) {
                    if (error.status === 0) {
                        console.error("[http] HTTP request received error 0");
                    }
                    observer.error(error);          // error in http request
                } else {
                    observer.error({ status: 0 });  // offline
                }
            },
            complete: () => {
                if (_subscription) {
                    _subscription.unsubscribe();
                }
            }            
        });
    }
    
    /********************************************/
    /* Browser -- use angular HttpClient        */
    /********************************************/
        
    private __angular_get(url: string, headers: any){
        const observable = new Observable((observer) => {
            try {
                this.angular.get(url, headers).pipe(take(1)).subscribe({
                    next: (data) => {
                        observer.next(data);
                    },
                    error: (error) => {
                        this._HttpError(observer, error);
                    },
                    complete: () => {
                        observer.complete();
                    }
                });
            }
            catch {
                observer.complete();
            }
        });
            
        return observable;
    }

    private __angular_post(url: string, data: any, headers: any){
        const observable = new Observable((observer) => {
            this.angular.post(url, data, headers).pipe(take(1)).subscribe({
                next: (data) => {
                    observer.next(data);
                },
                error: (error) => {
                    this._HttpError(observer, error);
                },
                complete: () => {
                    observer.complete();
                }
            });
        });
            
        return observable;
    }

    private __angular_check(url: string, headers: any){
        return this.angular.get(url, headers);
    }
    
    private __angular_head(url: string, headers: any) {
        const observable = new Observable(observer => {
            this.angular.head(url, { headers: headers, observe: 'response' }).pipe(take(1)).subscribe({
                next: (response) => {
                    if (response.status == 200) {
                        const _expectedContent = headers['headers'].get('Content-Type');
                        const _responseContent = response.headers.get('Content-Type');
    
                        observer.next(_expectedContent == _responseContent);
                        observer.complete();
                    } else {
                        observer.next(false);
                        observer.complete();
                    }    
                },
                error: (error) => {
                    this._HttpError(observer, error);
                },
                complete: () => {
                    observer.complete();
                }
            });
        });

        return observable;
    }

    /********************************************/
    /* Android / IOS -- use ionic HTTP          */
    /********************************************/
            
    private __cordova_get(url: string, headers: any, timeout: number | null, responseType: HttpResponseTypes | null){
        this.debuglog("[cordova] get url: " + url); 
        
        const observable = new Observable((observer) => {
            this.ionic.sendRequest(encodeURI(url), {
                method: 'get',
                headers: headers,
                timeout: (timeout === null) ? 60000 : timeout,
                responseType: responseType ? responseType : 'json'
            })
            .then(response => {
                this.debuglog("[cordova] reply to url: " + url, response);
                if (response.status == 200){
                    observer.next(response.data);
                    observer.complete();
                }
                else {
                    this._HttpError(observer, response);
                    observer.complete();
                }
            })
            .catch(response => {
                this.debuglog("[cordova] exteption", response);
                this._HttpError(observer, response);
                observer.complete();
            });
        });
        return observable;
    }
        
    private __cordova_post(url: string, data: any, headers: any, timeout: number | null, responseType: HttpResponseTypes | null, serializer: HttpSerializer){
        this.debuglog("[cordova] post url: " + url, data); 
        const observable = new Observable((observer) => {
            this.ionic.setDataSerializer(serializer);
            this.ionic.sendRequest(encodeURI(url), {
                method: 'post',
                data: data,
                headers: headers,
                timeout: (timeout === null) ? 60000 : timeout,
                responseType: responseType ? responseType : 'json'
            })
            .then(response => {
                this.debuglog("[cordova] reply to url: " + url, response);
                if (response.status == 200){
                    observer.next(response.data);
                    observer.complete();
                }
                else {
                    this._HttpError(observer, response);
                    observer.complete();
                }
            })
            .catch(response => {
                this.debuglog("[cordova] exteption", response);
                this._HttpError(observer, response);
                observer.complete();
            });
        });
        return observable;
    }
    
    private __cordova_check(url: string, headers: any){
        this.debuglog("[cordova] test url: " + url); 

        const observable = new Observable((observer) => {
            this.ionic.sendRequest(encodeURI(url), {
                method: 'get',
                headers: headers,
                timeout: 5
            })
            .then(response => {
                this.debuglog("[cordova] reply to url: " + url, response);
                if (response.status == 200){
                    observer.next(response.data);
                    observer.complete();
                }
                else {
                    observer.error(response);
                    observer.complete();
                }
            })
            .catch(response => {
                console.error(response); 
                this.debuglog("[cordova] exteption", response);
                
                observer.error(response);
                observer.complete();
            });
        });
        return observable;
    }

    private __cordova_head(url: string, headers: any){
        this.debuglog("[cordova] head url: " + url); 
        const observable = new Observable((observer) => {
            this.ionic.sendRequest(encodeURI(url), {
                method: 'head',
                headers: headers,
                timeout: 5
            })
            .then(response => {
                this.debuglog("[cordova] reply to url: " + url, response);
                if (response.status == 200){
                    const _expectedContent = headers['Content-Type'];
                    const _responseContent = response.headers['Content-Type'];

                    observer.next(_expectedContent == _responseContent);
                    observer.complete();                    
                }
                else {
                    observer.next(false);
                    observer.complete();
                }
            })
            .catch(response => {
                this.debuglog("[cordova] exteption", response);
                this._HttpError(observer, response);
                
                console.error(response); 
                observer.complete();
            });
        });
        return observable;
    }

    /********************************************/
    /* Public methods (platform dependant)      */
    /********************************************/
    
    private get offlineObservable(){
        const observable = new Observable((observer) => {
            setTimeout(() => {
                observer.error({ status: 0 });
                observer.complete();
            }, 0);
        });
        return observable;
    }
    
    /**
     * Performs an HTTP GET request.
     * @param url URL of the request.
     * @param withCredentials Indicates if credentials should be included.
     * @param headers Headers for the request.
     * @param responseType Expected response type.
     * @param timeout Timeout duration for the request in milliseconds.
     * @returns {Observable<any>} Observable with the HTTP GET response data.
     */    
    get(url: string, withCredentials = true, headers = httpService.headers, responseType: HttpResponseTypes | null = null, timeout:number | null = null){
        if (this.online.isOnline){
            if (this.platform.is('cordova')){
                return this.__cordova_get(url, headers, timeout, responseType);
            }

            const __angular_headers = {
                withCredentials: withCredentials,
                headers: new HttpHeaders(headers),
                timeout: ((timeout === null) ? 60000 : timeout).toString(),
                responseType: responseType ? responseType : 'json'
            };

            return this.__angular_get(url, __angular_headers);
        }
            
        return this.offlineObservable;   // offline
    }
        
    /**
     * Performs an HTTP POST request.
     * @param url URL of the request.
     * @param data Data to send in the request body.
     * @param headers Headers for the request.
     * @param responseType Expected response type.
     * @param serializer Serializer type for the data payload.
     * @param timeout Timeout duration for the request in milliseconds.
     * @returns {Observable<any>} Observable with the HTTP POST response data.
     */    
    post(url: string, data: any, headers = httpService.headers, responseType: HttpResponseTypes | null = null, serializer: HttpSerializer | null = null, timeout: number | null = null){
        if (!serializer){
            serializer = 'json';
        }

        if (this.online.isOnline){
            if (this.platform.is('cordova')){
                return this.__cordova_post(url, data, headers, timeout, responseType, serializer);
            }

            const __angular_headers = {
                withCredentials: true,
                headers: new HttpHeaders((serializer == 'multipart') ? {} : headers),
                timeout: ((timeout === null) ? 60000 : timeout).toString(),
                responseType: responseType ? responseType : 'json'
            };

            return this.__angular_post(url, data, __angular_headers);
        }

        return this.offlineObservable;   // offline
    }
    
    /**
     * Checks the server connection status by making a GET request to /online.
     * When state.place is set, appends ?place=<id>; the backend decides chain health (200 vs 503).
     * @returns {Observable<any>} Observable that emits connection status.
     */
    check(){
        let url = AppConstants.baseURL + 'online';
        if (this.state.place){
            url += '?place=' + this.state.place;
        }
        
        if (this.platform.is('cordova')){
            return this.__cordova_check(url, httpService.headers);
        }

        const __angular_headers = {
            withCredentials: true,
            headers: new HttpHeaders(httpService.headers)
        };
        
        return this.__angular_check(url, __angular_headers);
    }

    /**
     * Performs an HTTP HEAD request.
     * @param url URL of the request.
     * @param headers Headers for the request.
     * @returns {Observable<any>} Observable with the HTTP HEAD response data.
     */    
    head(url: string, headers: any = httpService.headers){
        if (this.platform.is('cordova')){
            return this.__cordova_head(url, headers);
        }

        const __angular_headers = {
            withCredentials: true,
            headers: new HttpHeaders(headers)
        };
        
        return this.__angular_head(url, __angular_headers);
    }
}

